import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { ToggleMinimizeSvg } from "../../../../../CommonComponents/svgs/Svgs";
import { FullScreenSvg, CompactSvg, GraphSvg, SyncSvg } from "./components/controls/ThermalHeatMapSvgs";
import CustomToolTip from "../../../../../CommonComponents/CustomToolTip";
import LayersPopup from "../../../../../CommonComponents/LayersPopup/LayersPopup";
import NewLoader from "../../../../../CommonComponents/NewLoader";
import ThermalHeatMap3dView from "./components/visualization/ThermalHeatMap3dView";
import ThermalHeatMapChart from "./components/charts/ThermalHeatMapChart";
import ThermalHeatMapSummary from "./components/panels/ThermalHeatMapSummary";
import { DEFAULT_THRESHOLDS, ThresholdConfig } from "./components/panels/ThermalAlertThresholds";
import { useThermalAlerts, alertHasBreachingSubsystem, getSubsystemsForFace } from "./hooks/useThermalAlerts";
import { SUBSYSTEM_NAME_TO_OPTION_ID } from "./components/visualization/thermalHeatmapSubsystemConfig";
import { buildAttitudeSeries, buildManeuverTrack, poseAtTime, thermalOrientationAt } from "./hooks/bodyAttitude";
import useThermalHeatmap from "../../../hooks/useThermalHeatmap";
import useAssets from "../../../hooks/useAssets";
import useThermalFilters from "./hooks/useThermalFilters";
import ThermalFilters from "./components/visualization/ThermalFilters";
import "./styles/ThermalHeatMap.scss";
import "./styles/ThermalControlsBar.scss";
import "./styles/ThermalFilters.scss";
import {
  useThermalHeatMapState,
} from "./hooks/ThermalHeatMapHooks";
import SafeMarginSection from "./components/margins/SafeMarginSection";
import { useThermalPdfExport, PdfExportParams } from "./components/exportpdf/ThermalPdfExport";

// View selection updated to use viewId
export type { FaceId, HeatmapRow } from "./hooks/ThermalHeatMapHooks";
export { formatTime } from "./hooks/ThermalHeatMapHooks";
export { ThermalViewSwitcherBtn };

// Custom hook for popup close-on-click-outside behavior
const usePopupCloseOutside = () => {
  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        const button = popupRef.current.querySelector("button.active");
        if (button) {
          (button as HTMLButtonElement).click();
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return popupRef;
};

// Helper to close active popup buttons in referenced elements
const closeActiveSiblingPopup = (ref: React.RefObject<HTMLDivElement>) => {
  const button = ref.current?.querySelector("button.active");
  if (button) {
    (button as HTMLButtonElement).click();
  }
};

interface ThermalViewSwitcherBtnProps {
  isSelectedView: { is3dView: boolean; isChartView: boolean; isMarginsView: boolean };
  onViewChange: (viewId: "3d" | "charts" | "margins") => void;
  onOpen?: () => void;
  placement?: "left" | "above" | "bottom" | "right";
  tooltipPlacement?: "left" | "top" | "bottom" | "right";
}

const ThermalViewSwitcherBtn: React.FC<ThermalViewSwitcherBtnProps> = ({
  isSelectedView,
  onViewChange,
  onOpen,
  placement = "bottom",
  tooltipPlacement = "bottom",
}) => {
  const popupRef = usePopupCloseOutside();

  const handleToggle = useCallback(
    (id: string) => {
      onViewChange(id as "3d" | "charts" | "margins");
      closeActiveSiblingPopup(popupRef);
    },
    [onViewChange]
  );

  const items = [
    { id: "3d", label: "3D View", checked: isSelectedView.is3dView },
    { id: "charts", label: "Charts View", checked: isSelectedView.isChartView },
    { id: "margins", label: "Margins View", checked: isSelectedView.isMarginsView }
  ];

  return (
    <div ref={popupRef}>
      <LayersPopup
        items={items}
        onToggle={handleToggle}
        title="View"
        placement={placement}
        renderTrigger={({ isOpen: popupIsOpen, onToggle }) => (
          <CustomToolTip title="Switch View" placement={tooltipPlacement}>
            <button
              className={`thm-pb-btn thm-pb-view-toggle thm-switch-view-btn ${popupIsOpen ? " active" : ""}`}
              onClick={() => {
                if (!popupIsOpen) onOpen?.();
                onToggle();
              }}
            >
              <GraphSvg active={popupIsOpen} />
            </button>
          </CustomToolTip>
        )}
      />
    </div>
  );
};

const clearAlertSelection = (setActiveThresholds: any, setSelectedNode: any, setSelectedBreach: any) => {
  setActiveThresholds(null);
  setSelectedNode(null);
  setSelectedBreach(null);
};

const findNodeMatchingTemp = (face: string, peakTemp: number | undefined, heatmapRows: any[], nodesPerFace: number): number | null => {
  if (!peakTemp || heatmapRows.length === 0) return null;
  const row = heatmapRows.find(r => r.time_sec === heatmapRows[0].time_sec);
  if (!row) return null;
  for (let n = 1; n <= nodesPerFace; n++) {
    if (row.nodes[`${face}_N${n}`] === peakTemp) return n;
  }
  return null;
};

const ThermalHeatMap = () => {
  // PDF params are read from a ref at click time, so no component stays mounted/re-built on every render
  const pdfParamsRef = useRef<PdfExportParams | null>(null);
  const getPdfParams = useCallback(() => pdfParamsRef.current, []);
  const { trigger: triggerPdfExport, isGenerating: isExportingPdf } = useThermalPdfExport(getPdfParams);

  const {
    isThermalMonitor,
    setIsThermalOpen,
    isMinimized,
    setIsMinimized,
    isCompact,
    isFullscreen,
    isSynced,
    isSelectedView,
    setIsSelectedView,
    heatmapRows,
    series,
    nodesPerFace,
    stepIndex,
    stepIndexOverride,
    resolvedStepIndex,
    jumpVersion,
    isPlaying,
    playbackSpeed,
    fetchStatus,
    isLastStep,
    handleToggleCompact,
    handleToggleSync,
    handleToggleFullscreen,
    handleTogglePlay,
    handleCycleSpeed,
    jumpToTime,
    resetOverride,
    summary,
    internalSummary,
    globalMin,
    globalMax,
    summaryItems,
    envParams,
    t0Epoch,
  } = useThermalHeatMapState();

  const [thresholds, setThresholds] = useState<ThresholdConfig[]>(DEFAULT_THRESHOLDS);
  // const [visibleFaces, setVisibleFaces] = useState<string[] | null>(null);
  const [activeThresholds, setActiveThresholds] = useState<ThresholdConfig[] | null>(null);
  const [selectedNode, setSelectedNode] = useState<number | null>(null);
  const [selectedBreach, setSelectedBreach] = useState<"high" | "low" | null>(null);
  const [zoomDomain, setZoomDomain] = useState<[number, number] | null>(null);

  const currentTimeEpoch = useMemo(() => {
    if (t0Epoch === null || !heatmapRows[resolvedStepIndex]) return null;
    return t0Epoch + heatmapRows[resolvedStepIndex].time_sec;
  }, [t0Epoch, heatmapRows, resolvedStepIndex]);

  const { thermalModel, currentOrbit, currentExposure, sunExposures, scenarioResponse, selectedGroupSatellite, orbitalInsights, presimOrbitDetails } = useThermalHeatmap(currentTimeEpoch);
  // Body orientation for the 3D view, snapped to right angles for the current step (undefined = default pose)
  const attitudeSeries = useMemo(
    () => buildAttitudeSeries(presimOrbitDetails?.quaternionData, presimOrbitDetails?.postionData, presimOrbitDetails?.availability),
    [presimOrbitDetails]
  );
  // Maneuver events for the run (built once per attitude series). The body holds still between them
  // and animates through each one, driven by the current simulation time.
  const maneuverTrack = useMemo(() => (attitudeSeries ? buildManeuverTrack(attitudeSeries) : []), [attitudeSeries]);
  const startPose = useMemo(() => (attitudeSeries ? thermalOrientationAt(attitudeSeries, attitudeSeries.times[0]) : undefined), [attitudeSeries]);
  const { alerts, isLoading: isAlertsLoading, evaluate } = useThermalAlerts(heatmapRows, thresholds, nodesPerFace, t0Epoch, thermalModel);
  const { schedule } = useAssets();

  const scheduleTasks = useMemo(
    () => (selectedGroupSatellite?.satelliteId ? schedule?.data?.[selectedGroupSatellite.satelliteId] ?? [] : []),
    [schedule, selectedGroupSatellite?.satelliteId]
  );

  pdfParamsRef.current = { heatmapRows, nodesPerFace, envParams, alerts, thermalModel, scenarioResponse, selectedGroupSatellite, orbitalInsights, t0Epoch, thresholds, sunExposures, scheduleTasks };

  const activeViewId = isSelectedView.is3dView ? "3d" : isSelectedView.isChartView ? "charts" : "margins";
  const viewMode = isFullscreen ? "fullscreen" : isCompact ? "compact" : "normal";
  const { active: filtersActive, categories: filtersCategories, placement: filtersPlacement, layerState, faceViews, renderingSettings, visibleFaces, chartDisplaySettings, sectionVisibilitySettings, panelVisibility, viewFiltersTitle, onActiveChange: onFiltersActiveChange, selectCategory: selectFiltersCategory, toggleOption: toggleFiltersOption, toggleAllOptions: toggleFiltersAllOptions, setFaceVisibility, setChartMode, showSubsystems, visibleSubsystems } = useThermalFilters(activeViewId, viewMode, attitudeSeries !== null);

  // Body pose for the 3D view: animated only while the attitude filter is on, otherwise the default pose
  const bodyQuaternion = useMemo(() => {
    if (!startPose || !layerState.showAttitudeAnimation || currentTimeEpoch === null) return undefined;
    return poseAtTime(maneuverTrack, currentTimeEpoch, startPose);
  }, [startPose, maneuverTrack, currentTimeEpoch, layerState.showAttitudeAnimation]);



  const handleViewChange = useCallback((viewId: "3d" | "charts" | "margins") => {
    setIsSelectedView({
      is3dView: viewId === "3d",
      isChartView: viewId === "charts",
      isMarginsView: viewId === "margins",
    });
  }, [setIsSelectedView]);

  const handleAlertClick = useCallback((alert: any) => {
    const goSubsystem = isFullscreen && alertHasBreachingSubsystem(alert, heatmapRows, thresholds);
    setFaceVisibility([alert.face]);
    if (goSubsystem) {
      setChartMode("subsystem");
      showSubsystems(
        getSubsystemsForFace(alert.face)
          .map((s) => SUBSYSTEM_NAME_TO_OPTION_ID[s.subsystemName])
          .filter(Boolean)
      );
      setSelectedNode(null);
      // Subsystem-level alerts carry a threshold-level key, not a ThresholdConfig id
      setActiveThresholds(
        alert.subsystemName ? null : thresholds.map(t => ({ ...t, enabled: t.id === alert.thresholdId }))
      );
    } else {
      if (panelVisibility.showSubsystemCharts) setChartMode("chart");
      setActiveThresholds(thresholds.map(t => ({ ...t, enabled: t.id === alert.thresholdId })));
      setSelectedNode(findNodeMatchingTemp(alert.face, alert.peakTemp, heatmapRows, nodesPerFace));
    }
    setSelectedBreach(alert.breach);
    jumpToTime(alert.timeSec);
  }, [isFullscreen, heatmapRows, thresholds, setFaceVisibility, setChartMode, showSubsystems, panelVisibility.showSubsystemCharts, nodesPerFace, jumpToTime]);

  const handleThresholdsChange = useCallback((t: ThresholdConfig[]) => {
    setThresholds(t);
    clearAlertSelection(setActiveThresholds, setSelectedNode, setSelectedBreach);
  }, []);

  return (
    <>
      {isThermalMonitor && (
        <>
          {/* Old always-mounted PDF renderer (remounted every render):
          <PdfExportComponent
            heatmapRows={heatmapRows}
            nodesPerFace={nodesPerFace}
            envParams={envParams}
            alerts={alerts}
            globalMin={globalMin ?? 0}
            globalMax={globalMax ?? 100}
            jumpVersion={jumpVersion}
            isPlaying={isPlaying}
            isLastStep={isLastStep}
            playbackSpeed={playbackSpeed}
            isSynced={isSynced}
            isSelectedView={isSelectedView}
            thermalModel={thermalModel}
            layerState={layerState}
            renderingSettings={renderingSettings}
            currentExposure={currentExposure}
            scenarioResponse={scenarioResponse}
            selectedGroupSatellite={selectedGroupSatellite}
            orbitalInsights={orbitalInsights}
            t0Epoch={t0Epoch}
            scheduleTasks={scheduleTasks}
            thresholds={thresholds}
            sunExposures={sunExposures}
          />
          */}
        <div className={`thermal-heatmap-monitor-container${isFullscreen ? " fullscreen" : isCompact ? " compact" : ""}`}> 
          <div className="thermal-heatmap-monitor-header">
            <span>Thermal Monitor</span>
            <div className="thermal-heatmap-monitor-header-buttons">
              {stepIndexOverride !== null && (
                <CustomToolTip title="Sync back to simulation clock" placement="top">
                  <button
                    className="thm-summary-reset-btn"
                    onClick={resetOverride}
                  >
                    <SyncSvg active={false} />
                  </button>
                </CustomToolTip>
              )}
              {!isCompact && isSelectedView.isChartView && (
                <CustomToolTip title="Reset Chart zoom" placement="top">
                  <button
                    className="thm-summary-reset-btn"
                    disabled={!zoomDomain}
                    onClick={() => setZoomDomain(null)}
                  >
                    <SyncSvg active={!!zoomDomain} />
                  </button>
                </CustomToolTip>
              )}
              <ThermalViewSwitcherBtn
                isSelectedView={isSelectedView}
                onViewChange={handleViewChange}
                placement="bottom"
                tooltipPlacement="top"
              />
              {!isCompact && (
                <ThermalFilters
                  active={filtersActive}
                  categories={filtersCategories}
                  viewFiltersTitle={viewFiltersTitle}
                  placement={filtersPlacement}
                  onActiveChange={onFiltersActiveChange}
                  onSelectCategory={selectFiltersCategory}
                  onToggleOption={toggleFiltersOption}
                  onToggleAllOptions={toggleFiltersAllOptions}
                />
              )}
              {!isCompact && (
                <CustomToolTip title={isExportingPdf ? "Generating PDF..." : "Export thermal report to PDF"} placement="top">
                  <button onClick={triggerPdfExport} disabled={isExportingPdf} className="thm-pb-btn" style={{ opacity: isExportingPdf ? 0.6 : 1, border: "none" }}>
                    PDF
                  </button>
                </CustomToolTip>
              )}
              <CustomToolTip title={isCompact ? "Expand view" : "Compact view"} placement="top">
                <button onClick={handleToggleCompact}>
                  <CompactSvg active={isCompact} />
                </button>
              </CustomToolTip>
              {!isCompact && (
                <CustomToolTip title={isFullscreen ? "Exit fullscreen" : "Fullscreen"} placement="top">
                  <button onClick={handleToggleFullscreen}>
                    <FullScreenSvg active={isFullscreen} />
                  </button>
                </CustomToolTip>
              )}

              {!isCompact && (
                <CustomToolTip title={isMinimized ? "Maximize" : "Minimize"} placement="top">
                  <button onClick={() => setIsMinimized(!isMinimized)}>
                    <ToggleMinimizeSvg type={isMinimized ? "maximize" : "minimize"} />
                  </button>
                </CustomToolTip>
              )}

            </div>
          </div>

          {!isMinimized && (
            <div className="thermal-heatmap-monitor-body">
              {(fetchStatus === "idle" || fetchStatus === "loading") && (
                <div className="thermal-heatmap-empty-state">
                  <NewLoader canvasHeight="100%" canvasWidth="100%" loaderpage="thermal-monitor" />
                  <span>Loading thermal data...</span>
                </div>
              )}
              {fetchStatus === "unavailable" && (
                <div className="thermal-heatmap-empty-state">
                  <span>Thermal data unavailable for this simulation.</span>
                </div>
              )}

              {heatmapRows.length > 0 && isSelectedView.is3dView && panelVisibility.showActiveView && (
                <ThermalHeatMap3dView
                  currentRow={series.getRow(resolvedStepIndex)}
                  bodyQuaternion={bodyQuaternion}
                  jumpVersion={jumpVersion}
                  nodesPerFace={nodesPerFace}
                  globalMin={globalMin!}
                  globalMax={globalMax!}
                  isPlaying={isPlaying}
                  isLastStep={isLastStep}
                  playbackSpeed={playbackSpeed}
                  isSynced={isSynced}
                  viewMode={isFullscreen ? "fullscreen" : isCompact ? "compact" : "normal"}
                  isSelectedView={isSelectedView}
                  thermalModel={thermalModel || undefined}
                  layerState={layerState}
                  faceViews={faceViews}
                  renderingSettings={renderingSettings}
                  currentExposure={currentExposure}
                  onToggleSync={handleToggleSync}
                  onTogglePlay={handleTogglePlay}
                  onCycleSpeed={handleCycleSpeed}
                  onViewChange={handleViewChange}
                />
              )}

              {heatmapRows.length > 0 && isSelectedView.isChartView && (panelVisibility.showActiveView || panelVisibility.showSubsystemCharts) && (
                <ThermalHeatMapChart
                  series={series}
                  nodesPerFace={nodesPerFace}
                  thresholds={activeThresholds || thresholds}
                  isFullscreen={isFullscreen}
                  isCompact={isCompact}
                  visibleFacesSet={visibleFaces}
                  chartDisplaySettings={chartDisplaySettings}
                  alerts={alerts}
                  selectedNode={selectedNode}
                  maxPoints={1000}
                  zoomDomain={zoomDomain}
                  onZoomDomainChange={setZoomDomain}
                  sunExposures={sunExposures}
                  t0Epoch={t0Epoch}
                  thermalModel={thermalModel}
                  visibleSubsystems={visibleSubsystems}
                  showSubsystemCharts={panelVisibility.showSubsystemCharts}
                />
              )}

              {heatmapRows.length > 0 && isSelectedView.isMarginsView && panelVisibility.showActiveView && (
                <SafeMarginSection
                  currentRow={series.getRow(resolvedStepIndex)}
                  thermalModel={thermalModel}
                  isFullscreen={isFullscreen}
                  sectionVisibilitySettings={sectionVisibilitySettings}
                />
              )}

              {heatmapRows.length > 0 && !isCompact && (
                <ThermalHeatMapSummary
                  isFullscreen={isFullscreen}
                  isCompact={isCompact}
                  panelVisibility={panelVisibility}
                  summaryItems={summaryItems}
                  summary={summary}
                  internalSummary={internalSummary}
                  envParams={envParams}
                  heatmapRows={heatmapRows}
                  alerts={alerts}
                  isAlertsLoading={isAlertsLoading}
                  thermalModel={thermalModel}
                  thresholds={thresholds}
                  activeThresholds={activeThresholds}
                  onThresholdsChange={handleThresholdsChange}
                  onEvaluateThresholds={evaluate}
                  onAlertClick={handleAlertClick}
                />
              )}
            </div>
          )}
        </div>
        </>
      )}
    </>
  );
};

export default ThermalHeatMap;
